import Div from "@jumbo/shared/Div";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Modal,
  Stack,
  Tab,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { makeFormikYupValidator } from "app/utils/validation";
// Tab components extracted
import MainTab from "./Tabs/MainTab";
import PricesTab from "./Tabs/PricesTab";
import CategoriesTab from "./Tabs/CategoriesTab";
import BindingsTab from "./Tabs/BindingsTab";
import CrossSellTab from "./Tabs/CrossSellTab";
import LoyaltySystemSection from "./Tabs/LoyaltySystemSection";
import DSOKProducts from "./DSOKProducts";
import ProductSelector from "../dsok-category/ProductSelector";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import { arrayMove } from "@dnd-kit/sortable";
import { getVatRates } from "app/utils/appHelpers";
// import { crossSellTestData } from "./test/crossSellTestData";
import {
  ALLERGENS_LIST,
  getLoyaltyTranslationCodePrefix,
} from "app/utils/constants/settings";
// import { updateProductCrossSell } from "app/services/api/product";
import { useSnackbar } from "notistack";
import { getBindings } from "app/services/api/bindings";
import { getAccessSystemZones } from "app/services/api/accessSystemZones";
import {
  DEFAULT_ZONE_ACCESS,
  normalizeZoneAccessFromProduct,
  sanitizeZoneAccessForPayload,
} from "./zoneAccessProductHelpers";
import DSOKProductAddNewBindingModal from "./DSOKProductAddNewBindingModal";

const allergensList = ALLERGENS_LIST();

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "75vw",
  maxHeight: "70vh",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  overflow: "auto",
};

function buildInitialLoyaltyData(product, loyaltyType) {
  const prefix = getLoyaltyTranslationCodePrefix(loyaltyType);
  const raw = product?.loyaltyData;
  const byLevel = {};
  if (Array.isArray(raw)) {
    raw.forEach((e) => {
      byLevel[e.level] = e;
    });
  }
  const basePrice = Number(product?.price);
  const roundPrice = (n) => (Number.isFinite(n) ? Number((n).toFixed(2)) : "");

  return [1, 2, 3].map((level) => {
    const entry = byLevel[level];
    const discountType =
      entry?.discountType === "percent" ? "percent" : "amount";
    const discountPercent =
      entry?.discountPercent !== undefined && entry?.discountPercent !== null
        ? entry.discountPercent
        : "";
    let discountedPrice =
      entry?.discountedPrice !== undefined && entry?.discountedPrice !== null
        ? entry.discountedPrice
        : "";
    if (
      discountType === "percent" &&
      Number.isFinite(basePrice) &&
      basePrice >= 0 &&
      Number.isFinite(Number(discountPercent)) &&
      Number(discountPercent) >= 0 &&
      Number(discountPercent) <= 100
    ) {
      discountedPrice = roundPrice(
        basePrice * (1 - Number(discountPercent) / 100),
      );
    }
    return {
      level,
      translationCode: prefix ? `${prefix}Level${level}` : `Level${level}`,
      isAvailable: entry?.isAvailable === true,
      discountType,
      discountPercent,
      discountedPrice,
    };
  });
}

const DSOKProductForm = ({
  product,
  onAddProduct,
  onEditProduct,
  onCancel,
  productLanguages,
  defaultLanguage,
  categories,
  locations,
  products,
  title,
  customerUiSettings,
  accessSystemEnabled,
  dsokType,
  crosssellActive,
  currencyCode,
  loyaltyEnabled,
  loyaltyType,
  datavisionPosEnabled,
  kitchenPrintingEnabled,
  kitchenPrintersGrouped,
  ticketPrintersGrouped,
  ticketPrintersLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const PRODUCT_DETAIL_TABS = ["main", "prices", "categories"];
  let enableBindings = false;

  if (customerUiSettings?.enableBindings || dsokType === "gastrokiosk") {
    PRODUCT_DETAIL_TABS.push("bindings");
    enableBindings = true;
  }

  if (crosssellActive) {
    PRODUCT_DETAIL_TABS.push("crossSell");
  }

  // Loyalty system tab — only when the customer has the loyalty system enabled.
  if (loyaltyEnabled) {
    PRODUCT_DETAIL_TABS.push("loyaltySystem");
  }

  // Individuálne nastavenia moved under Základné nastavenia (main) as sub-tab; no top-level tab

  const isValid5MinTime = (time) => {
    if (typeof time !== "string") return true;
    const m = time.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return true;
    const mm = Number(m[2]);
    return Number.isFinite(mm) ? mm % 5 === 0 : true;
  };
  const optionalNonNegativeNumber = () =>
    Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue == null ? null : value,
      )
      .nullable()
      .min(0, t("pages.dsokProducts.priceRequired"));

  const DSOKProductSchema = useMemo(
    () =>
      Yup.object().shape({
        productName: Yup.mixed()
          .required(t("pages.dsokCategories.nameRequired"))
          .test(
            "product-name-default-lang",
            t("pages.dsokCategories.nameRequired"),
            (v) =>
              v != null &&
              typeof v === "object" &&
              typeof v[defaultLanguage] === "string" &&
              v[defaultLanguage].trim().length > 0,
          ),
        price: optionalNonNegativeNumber(),
    timeBasedPrices: Yup.array().of(
      Yup.object().shape({
        timeFrom: Yup.string().test(
          "timeFrom-5min",
          t("pages.dsokProducts.timeBasedPricesValidation.step5"),
          (val) => isValid5MinTime(val),
        ),
        timeTo: Yup.string().test(
          "timeTo-5min",
          t("pages.dsokProducts.timeBasedPricesValidation.step5"),
          (val) => isValid5MinTime(val),
        ),
      }),
    ),
    productType: Yup.string().oneOf(["standard", "ticket"]).required(),
    ticketSettings: Yup.object().when("productType", {
      is: "ticket",
      then: (schema) =>
        schema
          .shape({
            validityType: Yup.string()
              .oneOf([
                "single_entry",
                "time_range",
                "from_first_use",
                "entry_count",
                "calendar_day",
              ])
              .required(
                t("pages.dsokProducts.ticketValidity.validation.typeRequired"),
              ),
            // Backend-managed types may still contain these fields,
            // but we don't validate them client-side while hidden from UI.
            validFrom: Yup.string().nullable(),
            validTo: Yup.string().nullable(),
            durationMinutes: Yup.number()
              .nullable()
              .when("validityType", {
                is: "from_first_use",
                then: (s) =>
                  s
                    .typeError(
                      t(
                        "pages.dsokProducts.ticketValidity.validation.durationMustBePositiveInt",
                      ),
                    )
                    .integer(
                      t(
                        "pages.dsokProducts.ticketValidity.validation.durationMustBePositiveInt",
                      ),
                    )
                    .moreThan(
                      0,
                      t(
                        "pages.dsokProducts.ticketValidity.validation.durationMustBePositiveInt",
                      ),
                    )
                    .required(
                      t(
                        "pages.dsokProducts.ticketValidity.validation.durationRequired",
                      ),
                    ),
                otherwise: (s) => s.nullable(),
              }),
            maxEntries: Yup.number().nullable(),
            templateId: Yup.number().nullable().oneOf([1, 2, null]),
          })
          .required(),
      otherwise: (schema) => schema.strip(),
    }),
      }),
    [t, defaultLanguage],
  );
  const validate = useMemo(
    () => makeFormikYupValidator(DSOKProductSchema),
    [DSOKProductSchema],
  );
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const [allProducts, setAllProducts] = useState({});
  const [openModal, setOpenModal] = useState();
  const [crossSellPickerOpen, setCrossSellPickerOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("main");
  const [mainSubTab, setMainSubTab] = useState("basicInfo");
  const [searchParams] = useSearchParams();
  const { hideDialog } = useJumboDialog();
  const [searchName, setSearchName] = useState();
  const [onlyActiveCategories, setOnlyActiveCategories] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [bindingsList, setBindingsList] = useState([]);

  const getValidationFieldLabel = (path) => {
    const normalizedPath = String(path || "");
    if (
      normalizedPath === "productName" ||
      normalizedPath.startsWith("productName.")
    ) {
      return `${t("pages.blocks.moduleName")} (${t(
        `languages.${defaultLanguage}`,
      )})`;
    }
    if (normalizedPath === "price") {
      return t("pages.products.price");
    }
    if (normalizedPath === "productType") {
      return t("pages.dsokProducts.productType.title");
    }
    if (normalizedPath === "ticketSettings.validityType") {
      return t("pages.dsokProducts.ticketValidity.typeLabel", {
        defaultValue: "Typ platnosti",
      });
    }
    if (normalizedPath === "ticketSettings.durationMinutes") {
      return t("pages.dsokProducts.ticketValidity.durationMinutes");
    }
    return normalizedPath;
  };

  const getValidationErrorPaths = (errors) => {
    if (!errors || typeof errors !== "object") return [];
    return Object.keys(errors).filter((key) => errors[key]);
  };

  // When true, the next submit saves but keeps the user on the product detail
  // (set by the "Save & stay" button, read back inside Formik's onSubmit).
  const submitAndStayRef = useRef(false);

  const handleOkClick = async (validateForm, submitForm, stay = false) => {
    submitAndStayRef.current = stay === true;
    const errors = await validateForm();
    const paths = getValidationErrorPaths(errors);
    if (paths.length > 0) {
      submitAndStayRef.current = false;
      const labels = paths.map(getValidationFieldLabel);
      enqueueSnackbar(
        `${t("messages.checkRequiredFields")}: ${labels.join(", ")}`,
        { variant: "warning" },
      );
      return;
    }
    await submitForm();
  };

  useEffect(() => {
    if (!enableBindings) return;
    getBindings()
      .then((list) => setBindingsList(Array.isArray(list) ? list : []))
      .catch(() => setBindingsList([]));
  }, [enableBindings]);
  const handleDelete = (key, values, setFieldValue) => {
    const bindings = { ...values.bindings };
    delete bindings[key];
    setFieldValue("bindings", {
      ...bindings,
    });
    hideDialog();
  };

  useEffect(() => {
    if (defaultLanguage) {
      setSelectedLanguage(defaultLanguage);
    }
  }, [defaultLanguage]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "individualSettings") {
      setSelectedTab("main");
      if (accessSystemEnabled === true) {
        setMainSubTab("individualSettings");
      }
    } else if (tab === "allergens") {
      setSelectedTab("main");
      setMainSubTab("parameters");
    }
  }, [searchParams, accessSystemEnabled]);

  useEffect(() => {
    if (products?.length) {
      const productMap = {};
      products.forEach((product) => (productMap[product._id] = product));
      setAllProducts(productMap);
    }
  }, [products]);

  useEffect(() => {
    if (categories?.length) {
      let filteredCategories = searchName
        ? categories.filter(
            (category) =>
              category?.name &&
              category.name[defaultLanguage].toLowerCase().includes(searchName),
          )
        : categories;
      if (onlyActiveCategories) {
        filteredCategories = filteredCategories.filter((category) =>
          product?.categories ? product?.categories[category._id] : false,
        );
      }
      setFilteredCategories(filteredCategories || []);
    }
  }, [
    searchName,
    categories,
    onlyActiveCategories,
    defaultLanguage,
    product?.categories,
  ]);

  const handleAddGroup = (code, values, setFieldValue) => {
    const binding = code && values?.bindings?.[code] ? values.bindings[code] : null;
    if (binding) {
      setFieldValue("bindings.name", binding.name);
      setFieldValue("bindings.min", binding.min);
      setFieldValue("bindings.max", binding.max);
      setFieldValue("bindings.bindingId", binding.bindingId);
      setFieldValue("bindings.required", Boolean(binding.required));
    }
    // Enrich initialBinding with name from bindings list when product binding has bindingId but no/empty name
    let bindingToPass = binding || undefined;
    if (binding?.bindingId && Array.isArray(bindingsList) && bindingsList.length > 0) {
      const hasName =
        binding.name &&
        typeof binding.name === "object" &&
        Object.values(binding.name).some((v) => v != null && String(v).trim() !== "");
      if (!hasName) {
        const found = bindingsList.find((b) => String(b._id) === String(binding.bindingId));
        if (found) {
          const src =
            (found.bindingName && typeof found.bindingName === "object" && found.bindingName) ||
            (found.name && typeof found.name === "object" && found.name) ||
            {};
          const nameObj = { sk: src.sk || src.SK || "", en: src.en || src.EN || "" };
          if (nameObj.sk || nameObj.en) {
            bindingToPass = { ...binding, name: nameObj };
          }
        }
      }
    }
    setOpenModal({ type: "addGroup", value: code || null, binding: bindingToPass });
  };

  const handleAddBinding = (code) => {
    setOpenModal({ type: "addBinding", value: code });
  };

  const handleSortEnd = (oldIndex, newIndex, bindings, setFieldValue) => {
    const orderedBindings = {};
    delete bindings.min;
    delete bindings.max;
    const sortedBindingList = Object.keys(bindings)
      .map((code) => ({
        code,
        ...bindings[code],
      }))
      .sort((b1, b2) => b1.index - b2.index);
    arrayMove(sortedBindingList, oldIndex, newIndex).forEach(
      (binding, index) =>
        (orderedBindings[binding.code] = { ...binding, index }),
    );
    setFieldValue("bindings", orderedBindings);
  };

  const handleChipSortEnd = (
    bindingCode,
    oldIndex,
    newIndex,
    bindings,
    setFieldValue,
  ) => {
    const updatedBindings = { ...bindings };
    const binding = updatedBindings[bindingCode];

    if (binding && binding.products) {
      const productKeys = Object.keys(binding.products).filter(
        (key) => binding.products[key] && binding.products[key].status,
      );

      const reorderedProducts = arrayMove(productKeys, oldIndex, newIndex);

      // Create new products object with reordered keys
      const newProducts = {};
      reorderedProducts.forEach((productKey, index) => {
        newProducts[productKey] = binding.products[productKey];
      });

      // Add back inactive products
      Object.keys(binding.products).forEach((key) => {
        if (!binding.products[key] || !binding.products[key].status) {
          newProducts[key] = binding.products[key];
        }
      });

      updatedBindings[bindingCode] = {
        ...binding,
        products: newProducts,
      };

      setFieldValue("bindings", updatedBindings);
    }
  };

  const onAddGroup = (
    { id, name, min, max, required, bindingId },
    { values, setFieldValue },
  ) => {
    const bindings = { ...(values.bindings || {}) };
    const selectedBindingId = bindingId ?? values?.bindings?.bindingId;

    const payload = {
      bindingId:
        selectedBindingId ?? (id && bindings[id] ? bindings[id].bindingId : ""),
      name: name ?? values?.bindings?.name ?? {},
      min: min ?? values?.bindings?.min ?? 0,
      max: max ?? values?.bindings?.max ?? 0,
      required: Boolean(
        required ?? values?.bindings?.required ?? bindings[id]?.required,
      ),
    };

    if (id && bindings[id]) {
      bindings[id] = { ...bindings[id], ...payload };
    } else {
      const EXCLUDED_TEMP_KEYS = [
        "name",
        "min",
        "max",
        "bindingId",
        "required",
      ];
      const existingCodes = Object.keys(bindings).filter(
        (k) => !EXCLUDED_TEMP_KEYS.includes(k),
      );
      // Prefer numeric incremental ids if current ids are numeric, otherwise timestamped id
      const numericIds = existingCodes
        .map((k) => (Number.isFinite(Number(k)) ? Number(k) : null))
        .filter((n) => n !== null);
      let newId;
      if (numericIds.length === existingCodes.length) {
        const next = numericIds.length ? Math.max(...numericIds) + 1 : 0;
        newId = String(next);
      } else {
        newId = `group-${Date.now()}`;
      }
      bindings[newId] = { ...payload, index: existingCodes.length };
    }

    // cleanup temporary fields used by the modal form (root-level only)
    delete bindings.name;
    delete bindings.min;
    delete bindings.max;
    delete bindings.required;
    delete bindings.bindingId;

    setFieldValue("bindings", bindings);
    setOpenModal();
  };
  const loggedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const vatRates = getVatRates(
    loggedUser?.customer?.dsokSettings?.customer?.vatCountryCode,
  );
  const isGastro =
    loggedUser?.customer?.dsokSettings?.customer?.lineOfBusiness === "gastro";
  return (
    <Formik
      validateOnChange={true}
      validate={validate}
      initialValues={{
        _id: product?._id || "",
        isActive: product?.isActive || "",
        isGlutenFree: product?.isGlutenFree || false,
        displayValidityDate: product?.displayValidityDate || false,
        isTakeaway: product?.isTakeaway || false,
        vatRateTakeaway: product?.vatRateTakeaway || "",
        timeBasedPrices: Array.isArray(product?.timeBasedPrices)
          ? product.timeBasedPrices.map((r) => ({
              days: Array.isArray(r?.days) ? r.days : r?.day ? [r.day] : [],
              timeFrom: typeof r?.timeFrom === "string" ? r.timeFrom : "00:00",
              timeTo: typeof r?.timeTo === "string" ? r.timeTo : "00:00",
              type: r?.type || "amount",
              label: r?.label || "none",
              value:
                typeof r?.value === "number" ? r.value : Number(r?.value) || 0,
            }))
          : [],
        allergens: (() => {
          const raw = product?.allergens;
          if (Array.isArray(raw)) {
            // Supports arrays of numbers/strings or objects with { number }
            return raw
              .map((item) => {
                if (typeof item === "number") return item;
                if (typeof item === "string") return Number(item);
                if (item && typeof item === "object") {
                  const num = Number(item.number);
                  return Number.isNaN(num) ? undefined : num;
                }
                return undefined;
              })
              .filter((n) => typeof n === "number" && !Number.isNaN(n));
          }
          if (typeof raw === "string" && raw.trim()) {
            try {
              const parsed = JSON.parse(raw);
              if (Array.isArray(parsed)) {
                return parsed
                  .map((item) =>
                    typeof item === "object" && item
                      ? Number(item.number)
                      : Number(item),
                  )
                  .filter((n) => typeof n === "number" && !Number.isNaN(n));
              }
            } catch (e) {
              return raw
                .split(",")
                .map((s) => Number(s.trim()))
                .filter((n) => !Number.isNaN(n));
            }
          }
          if (raw && typeof raw === "object") {
            return Object.keys(raw)
              .map((k) => Number(k))
              .filter((n) => !Number.isNaN(n));
          }
          return [];
        })(),
        productName: (() => {
          const raw = product?.productName;
          const langs = Array.isArray(productLanguages)
            ? productLanguages
            : [];
          let base = {};
          if (raw && typeof raw === "object" && !Array.isArray(raw)) {
            base = { ...raw };
          } else if (typeof raw === "string") {
            base = { [defaultLanguage]: raw };
          }
          const out = { ...base };
          const keys =
            langs.length > 0 ? langs : defaultLanguage ? [defaultLanguage] : [];
          for (const lang of keys) {
            if (out[lang] == null) out[lang] = "";
          }
          return out;
        })(),
        productDescription: product?.productDescription || "",
        configuration: product?.configuration || "",
        price: product?.price || "",
        salePrice: product?.salePrice || "",
        categories: product?.categories || {},
        bindings: product?.bindings || {},
        image: product?.image || "",
        vatRate: product?.vatRate || "",
        stock: product?.stock || "",
        customProductId: product?.customProductId || "",
        sku: product?.sku || "",
        customProductImageUrl: product?.customProductImageUrl || "",
        crossSell: product?.crossSell || {},
        expirationDate: product?.expirationDate ?? null,
        labels: product?.labels || "",
        packagePrice: product?.packagePrice || 0,
        isSalePrice: product?.isSalePrice || false,
        discountPercent: product?.discountPercent || 0,
        productType: product?.productType || "standard",
            ticketSettings: {
          validityType: product?.ticketSettings?.validityType || "",
          validFrom: product?.ticketSettings?.validFrom || null,
          validTo: product?.ticketSettings?.validTo || null,
          durationMinutes:
            typeof product?.ticketSettings?.durationMinutes === "number"
              ? product.ticketSettings.durationMinutes
              : null,
          maxEntries:
            typeof product?.ticketSettings?.maxEntries === "number"
              ? product.ticketSettings.maxEntries
              : null,
          timeFrom:
            typeof product?.ticketSettings?.timeFrom === "string" && product.ticketSettings.timeFrom
              ? product.ticketSettings.timeFrom
              : "00:00",
          timeTo:
            typeof product?.ticketSettings?.timeTo === "string" && product.ticketSettings.timeTo
              ? product.ticketSettings.timeTo
              : "23:59",
          printerIds: Array.isArray(product?.ticketSettings?.printerIds)
            ? product.ticketSettings.printerIds
                .map((id) => String(id).trim())
                .filter((id) => id !== "")
            : [],
          templateId:
            Number(product?.ticketSettings?.templateId) === 1 ||
            Number(product?.ticketSettings?.templateId) === 2
              ? Number(product.ticketSettings.templateId)
              : null,
        },
        zoneAccess: normalizeZoneAccessFromProduct(product),
        loyaltyData: buildInitialLoyaltyData(
          product,
          loyaltyType || "none",
        ),
        loyaltySystem: {
          points:
            product?.loyaltySystem?.points != null &&
            product.loyaltySystem.points !== ""
              ? product.loyaltySystem.points
              : "",
        },
        productParameters: (() => {
          const p = product?.productParameters;
          const toNum = (v) => {
            if (v == null || v === "") return null;
            const n = Number(v);
            return Number.isFinite(n) ? Number((n).toFixed(2)) : null;
          };
          return {
            volumeLiters: toNum(p?.volumeLiters),
            weightKg: toNum(p?.weightKg),
            lengthMm: toNum(p?.lengthMm),
            widthMm: toNum(p?.widthMm),
            heightMm: toNum(p?.heightMm),
          };
        })(),
        tempParameter: {
          autoAddBindingsToOrder:
            product?.tempParameter?.autoAddBindingsToOrder === true,
        },
        productKitchenPrinting: (() => {
          const pk = product?.productKitchenPrinting;
          if (pk && typeof pk === "object") {
            const mode =
              pk.mode === "selected" && Array.isArray(pk.printerIds) && pk.printerIds.length > 0
                ? "selected"
                : "none";
            const printerIds =
              mode === "selected" && Array.isArray(pk.printerIds)
                ? pk.printerIds.filter((id) => id != null && String(id).trim() !== "")
                : [];
            return { mode, printerIds };
          }
          return { mode: "none", printerIds: [] };
        })(),
      }}
      // validation handled by validate above
      onSubmit={async (data, formikBag) => {
        const value = { ...data };
        const { setFieldError } = formikBag || {};

        if (
          value.expirationDate == null ||
          String(value.expirationDate).trim() === ""
        ) {
          delete value.expirationDate;
        }

        // Loyalty: validate percent levels before other logic
        if (
          loyaltyEnabled === true &&
          loyaltyType &&
          loyaltyType !== "none" &&
          setFieldError
        ) {
          const loyaltyData = Array.isArray(data.loyaltyData)
            ? data.loyaltyData
            : [];
          for (let level = 1; level <= 3; level++) {
            const index = level - 1;
            const entry =
              loyaltyData[index] ||
              loyaltyData.find((e) => e?.level === level);
            if (entry?.discountType === "percent") {
              const pct = entry.discountPercent;
              const num =
                pct !== "" &&
                pct !== undefined &&
                pct !== null
                  ? Number(pct)
                  : NaN;
              if (!Number.isFinite(num) || num < 0 || num > 100) {
                setFieldError(
                  `loyaltyData.${index}.discountPercent`,
                  t("pages.dsokProducts.loyaltyProgram.validation.percentRequired"),
                );
                return;
              }
            }
          }
        }

        // Normalize allergens to numbers
        if (Array.isArray(value.allergens)) {
          value.allergens = value.allergens
            .map((keyOrNum) => {
              if (typeof keyOrNum === "number") return keyOrNum;
              const key = String(keyOrNum);
              const num = allergensList[key]?.number;
              return typeof num === "number" ? num : Number(keyOrNum);
            })
            .filter((n) => typeof n === "number" && !Number.isNaN(n));
        }
        delete data.bindings.name;
        delete value.printers;

        // Ticket validity settings: include only for productType === "ticket"
        if (value.productType === "ticket") {
          const ts = value.ticketSettings || {};
          // If validity type is currently backend-managed (hidden in UI),
          // preserve settings as-is unless user explicitly changes the type.
          const normalizedPrinterIds = Array.isArray(ts.printerIds)
            ? ts.printerIds
                .map((id) => String(id).trim())
                .filter((id) => id !== "")
            : [];

          if (ts.validityType === "time_range" || ts.validityType === "entry_count") {
            value.ticketSettings = {
              ...ts,
              printerIds: normalizedPrinterIds,
              templateId:
                Number(ts.templateId) === 1 || Number(ts.templateId) === 2
                  ? Number(ts.templateId)
                  : null,
            };
          } else {
            const out = {
              validityType: ts.validityType || "",
              validFrom: null,
              validTo: null,
              durationMinutes: null,
              maxEntries: null,
              printerIds: normalizedPrinterIds,
              templateId:
                Number(ts.templateId) === 1 || Number(ts.templateId) === 2
                  ? Number(ts.templateId)
                  : null,
            };
            if (ts.validityType === "from_first_use") {
              out.durationMinutes =
                typeof ts.durationMinutes === "number"
                  ? ts.durationMinutes
                  : null;
            }
            if (ts.validityType === "calendar_day") {
              out.timeFrom =
                typeof ts.timeFrom === "string" && ts.timeFrom ? ts.timeFrom : "00:00";
              out.timeTo =
                typeof ts.timeTo === "string" && ts.timeTo ? ts.timeTo : "23:59";
            }
            value.ticketSettings = out;
          }
          let zonesList = [];
          try {
            zonesList = await getAccessSystemZones();
          } catch (_e) {
            zonesList = [];
          }
          value.zoneAccess = sanitizeZoneAccessForPayload(
            value.zoneAccess,
            zonesList,
          );
        } else {
          delete value.ticketSettings;
          delete value.zoneAccess;
        }

        if (loyaltyEnabled === true && loyaltyType && loyaltyType !== "none") {
          const prefix = getLoyaltyTranslationCodePrefix(loyaltyType);
          const basePrice = Number(data.price);
          const roundPrice = (n) =>
            Number.isFinite(n) ? Number((n).toFixed(2)) : undefined;

          value.loyaltyData = [1, 2, 3].map((level) => {
            const entry = Array.isArray(data.loyaltyData)
              ? data.loyaltyData[level - 1] ||
                data.loyaltyData.find((e) => e?.level === level)
              : undefined;
            const discountType =
              entry?.discountType === "percent" ? "percent" : "amount";
            let discountedPrice =
              entry?.discountedPrice !== undefined &&
              entry?.discountedPrice !== null
                ? Number(entry.discountedPrice)
                : undefined;

            if (discountType === "percent") {
              const pct = entry?.discountPercent;
              const pctNum =
                pct !== "" && pct !== undefined && pct !== null
                  ? Number(pct)
                  : NaN;
              if (
                Number.isFinite(pctNum) &&
                pctNum >= 0 &&
                pctNum <= 100 &&
                Number.isFinite(basePrice) &&
                basePrice >= 0
              ) {
                discountedPrice = roundPrice(
                  basePrice * (1 - pctNum / 100),
                );
              }
            }

            const out = {
              level,
              translationCode: `${prefix}Level${level}`,
              isAvailable: entry?.isAvailable === true,
              discountType,
              ...(Number.isFinite(discountedPrice)
                ? { discountedPrice }
                : {}),
            };
            if (discountType === "percent" && entry?.discountPercent !== undefined && entry?.discountPercent !== "" && entry?.discountPercent !== null) {
              const pctNum = Number(entry.discountPercent);
              if (Number.isFinite(pctNum)) out.discountPercent = pctNum;
            }
            return out;
          });
        } else {
          delete value.loyaltyData;
        }

        // Loyalty system points (earned per purchase) — stored only when the loyalty
        // system is enabled; normalized to a non-negative integer.
        if (loyaltyEnabled === true) {
          const rawPoints = data?.loyaltySystem?.points;
          const n =
            rawPoints === "" || rawPoints == null ? 0 : Number(rawPoints);
          value.loyaltySystem = {
            points: Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0,
          };
        } else {
          delete value.loyaltySystem;
        }

        if (value.productParameters && typeof value.productParameters === "object") {
          const toNum = (v) => {
            if (v == null || v === "") return null;
            const n = Number(v);
            return Number.isFinite(n) ? Number((n).toFixed(2)) : null;
          };
          value.productParameters = {
            volumeLiters: toNum(value.productParameters.volumeLiters),
            weightKg: toNum(value.productParameters.weightKg),
            lengthMm: toNum(value.productParameters.lengthMm),
            widthMm: toNum(value.productParameters.widthMm),
            heightMm: toNum(value.productParameters.heightMm),
          };
        }

        if (value.productKitchenPrinting && typeof value.productKitchenPrinting === "object") {
          const pk = value.productKitchenPrinting;
          const printerIds = Array.isArray(pk.printerIds) ? pk.printerIds : [];
          value.productKitchenPrinting = {
            mode: printerIds.length > 0 ? "selected" : "none",
            printerIds,
          };
        }

        const handler = product ? onEditProduct : onAddProduct;
        const stay = submitAndStayRef.current === true;
        submitAndStayRef.current = false;
        handler(value, { stay });
      }}
    >
      {({ setFieldValue, submitForm, validateForm, values }) => (
        <Form style={{ textAlign: "left", maxHeight: "100%" }} noValidate>
          <Typography variant="h4" pl={2} pb={2} mt={0}>
            {values.productName?.[defaultLanguage] || ""}
          </Typography>
          <TabContext value={selectedTab} sx={{ maxHeight: "100%" }}>
            <Stack sx={{ mb: 2, maxHeight: "100%" }}>
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <TabList onChange={(_, val) => setSelectedTab(val)}>
                  {PRODUCT_DETAIL_TABS.map((tab) => {
                    return (
                      <Tab
                        key={tab}
                        label={t(`pages.dsokProducts.${tab}`)}
                        value={tab}
                      />
                    );
                  })}
                </TabList>
              </Box>
              <TabPanel
                key="main"
                value="main"
                sx={{ pb: 1, pr: 0, maxHeight: "60vh", overflow: "auto" }}
              >
                <MainTab
                  values={values}
                  setFieldValue={setFieldValue}
                  cashRegisterCode={
                    product?.additionalInformation?.cashRegisterCode || ""
                  }
                  productLanguages={productLanguages}
                  selectedLanguage={selectedLanguage}
                  setSelectedLanguage={setSelectedLanguage}
                  defaultLanguage={defaultLanguage}
                  t={t}
                  locations={locations}
                  customerUiSettings={customerUiSettings}
                  accessSystemEnabled={accessSystemEnabled}
                  isGastro={isGastro}
                  allergensList={allergensList}
                  basicSettingsSubTab={mainSubTab}
                  setBasicSettingsSubTab={setMainSubTab}
                  datavisionPosEnabled={datavisionPosEnabled}
                  kitchenPrintingEnabled={kitchenPrintingEnabled}
                  kitchenPrintersGrouped={kitchenPrintersGrouped}
                  ticketPrintersGrouped={ticketPrintersGrouped}
                  ticketPrintersLoading={ticketPrintersLoading}
                />
              </TabPanel>
              <TabPanel
                key="prices"
                value="prices"
                sx={{ pb: 1, pr: 0, maxHeight: "60vh", overflow: "auto" }}
              >
                <PricesTab
                  values={values}
                  setFieldValue={setFieldValue}
                  t={t}
                  vatRates={vatRates}
                  isGastro={isGastro}
                  loyaltyEnabled={loyaltyEnabled}
                  loyaltyType={loyaltyType}
                  currencyCode={currencyCode}
                />
              </TabPanel>
              <TabPanel
                key="categories"
                value="categories"
                sx={{ pb: 1, height: "60vh", pr: 0 }}
              >
                <CategoriesTab
                  t={t}
                  filteredCategories={filteredCategories}
                  searchName={searchName}
                  setSearchName={setSearchName}
                  onlyActiveCategories={onlyActiveCategories}
                  setOnlyActiveCategories={setOnlyActiveCategories}
                  product={product}
                  defaultLanguage={defaultLanguage}
                />
              </TabPanel>
              {enableBindings && (
                <TabPanel
                  key="bindings"
                  value="bindings"
                  sx={{
                    pb: 1,
                    pr: 0,
                    maxHeight: "60vh",
                    height: "60vh",
                    overflow: "auto",
                  }}
                >
                  <BindingsTab
                    t={t}
                    values={values}
                    setFieldValue={setFieldValue}
                    handleSortEnd={handleSortEnd}
                    handleChipSortEnd={handleChipSortEnd}
                    handleAddGroup={(code, v, s) => {
                      if (code || v || s) {
                        handleAddGroup(code, v, s);
                      } else {
                        setOpenModal({ type: "addGroup" });
                      }
                    }}
                    handleAddBinding={handleAddBinding}
                    handleDelete={handleDelete}
                    allProducts={allProducts}
                    defaultLanguage={defaultLanguage}
                    currencyCode={currencyCode}
                  />
                </TabPanel>
              )}
              {crosssellActive && (
                <TabPanel
                  key="crossSell"
                  value="crossSell"
                  sx={{ pb: 1, pr: 0, maxHeight: "60vh", overflow: "auto" }}
                >
                  <CrossSellTab
                    t={t}
                    values={values}
                    setFieldValue={setFieldValue}
                    i18n={i18n}
                    defaultLanguage={defaultLanguage}
                    currencyCode={currencyCode}
                    setCrossSellPickerOpen={setCrossSellPickerOpen}
                  />
                </TabPanel>
              )}
              {loyaltyEnabled && (
                <TabPanel
                  key="loyaltySystem"
                  value="loyaltySystem"
                  sx={{ pb: 1, pr: 0, maxHeight: "60vh", overflow: "auto" }}
                >
                  <LoyaltySystemSection
                    values={values}
                    setFieldValue={setFieldValue}
                    t={t}
                  />
                </TabPanel>
              )}

              </Stack>
          </TabContext>
          <Div
            sx={{
              mt: 2,
              mb: 1,
              mx: 2,
              gap: "4px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button size="normal" onClick={onCancel}>
              {t("buttons.cancel")}
            </Button>
            <Button
              type="button"
              variant="outlined"
              size="normal"
              onClick={() => handleOkClick(validateForm, submitForm, true)}
            >
              {t("buttons.saveAndStay")}
            </Button>
            <Button
              type="button"
              variant="contained"
              size="normal"
              onClick={() => handleOkClick(validateForm, submitForm, false)}
            >
              {t("buttons.save")}
            </Button>
          </Div>
          <Modal open={openModal !== undefined} onClose={() => setOpenModal()}>
            <Div sx={modalStyle}>
              {openModal?.type === "addGroup" && (
                <DSOKProductAddNewBindingModal
                  productLanguages={productLanguages}
                  selectedLanguage={selectedLanguage}
                  onSelectLanguage={(val) => setSelectedLanguage(val)}
                  values={values}
                  setFieldValue={setFieldValue}
                  onCancel={() => setOpenModal()}
                  editingId={openModal?.value}
                  initialBinding={openModal?.binding}
                  onSave={(override) =>
                    onAddGroup(
                      {
                        id: openModal.value,
                        name:
                          override?.name !== undefined
                            ? override.name
                            : values.bindings.name,
                        min:
                          override?.min !== undefined
                            ? override.min
                            : values.bindings.min,
                        max:
                          override?.max !== undefined
                            ? override.max
                            : values.bindings.max,
                        required:
                          override?.required !== undefined
                            ? override.required
                            : values.bindings.required,
                        bindingId:
                          override?.bindingId !== undefined
                            ? override.bindingId
                            : values.bindings.bindingId,
                      },
                      {
                        values,
                        setFieldValue,
                      },
                    )
                  }
                />
              )}
              {openModal?.type === "addBinding" && (
                <Div
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Div sx={{ flex: 1, overflowY: "scroll", maxHeight: "60vh" }}>
                    <DSOKProducts
                      selectMode
                      onItemUpdate={(id, field, val) => {
                        let item =
                          values?.bindings &&
                          values?.bindings[openModal.value] &&
                          values?.bindings[openModal.value].products
                            ? values.bindings[openModal.value]?.products[id]
                            : {};
                        if (typeof item !== "object") {
                          item = {};
                        }
                        item[field] = val;
                        setFieldValue(`bindings.${openModal.value}.products`, {
                          ...(values.bindings[openModal.value]?.products || {}),
                          [id]: { ...item },
                        });
                      }}
                      selectedProducts={
                        values.bindings[openModal.value]?.products || {}
                      }
                    />
                  </Div>

                  <Div
                    sx={{
                      mt: 2,
                      mb: 1,
                      mx: 2,
                      gap: "4px",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button size="normal" onClick={() => setOpenModal()}>
                      {t("buttons.cancel")}
                    </Button>
                    <Button
                      variant="contained"
                      size="normal"
                      onClick={() => setOpenModal()}
                    >
                      {t("buttons.ok")}
                    </Button>
                  </Div>
                </Div>
              )}
            </Div>
          </Modal>
          <Modal
            open={!!crossSellPickerOpen}
            onClose={() => setCrossSellPickerOpen(false)}
          >
            <Div sx={modalStyle}>
              <ProductSelector
                setFieldValue={(field, value) => {
                  if (field === "products") {
                    const toAdd = Object.keys(value || {}).map((id) => {
                      const prod = value[id] || {};
                      return {
                        productId: id,
                        id,
                        name:
                          prod.productName?.[defaultLanguage] ||
                          prod.productName ||
                          prod.metadata?.name?.[defaultLanguage] ||
                          prod.metadata?.name ||
                          prod.name ||
                          "",
                        price: prod.metadata?.price || prod.price || 0,
                        imageUrl:
                          prod.image ||
                          prod.customProductImageUrl ||
                          prod.metadata?.image,
                      };
                    });
                    const existing = values.crossSell.items || [];
                    const merged = [...existing];
                    toAdd.forEach((p) => {
                      if (!merged.find((x) => (x.id || x.productId) === p.id)) {
                        merged.push({ ...p, order: merged.length });
                      }
                    });
                    setFieldValue("crossSell.items", merged);
                  }
                }}
                values={{}}
                setCloseModal={() => setCrossSellPickerOpen(false)}
                categoryId={null}
                products={products}
              />
            </Div>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default DSOKProductForm;
